import type { UserDeletionImpact, UserResponse } from '@shared/schemas';
import { all, call, getContext, put, takeLatest } from 'redux-saga/effects';

import type { AdministratorsGateway } from '../infrastructure/administrators.gateway';
import {
  administratorsRequested,
  archiveToggleRequested,
  deleteRequested,
  deletionImpactRequested,
} from './administrators.actions';
import { administratorsActions } from './administrators.slice';

const LOAD_ERROR = 'Failed to load administrators.';
const ARCHIVE_ERROR = 'Failed to update user.';
const DELETE_ERROR = 'Failed to delete this administrator.';

/** Best-effort human message for the failure banner / snackbar (no domain error union). */
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/** WORKER: fetch every administrator. All I/O via the injected gateway. */
function* fetchWorker() {
  const gateway: AdministratorsGateway = yield getContext('administratorsGateway');
  yield put(administratorsActions.loadStarted());
  try {
    const rows = (yield call(gateway.fetchAdministrators)) as UserResponse[];
    yield put(administratorsActions.loadSucceeded(rows));
  } catch (e) {
    yield put(administratorsActions.loadFailed(toMessage(e, LOAD_ERROR)));
  }
}

/** WORKER: archive / unarchive a row, patching it in place on success. */
function* archiveWorker(action: ReturnType<typeof archiveToggleRequested>) {
  const { id, archived } = action.payload;
  const gateway: AdministratorsGateway = yield getContext('administratorsGateway');
  yield put(administratorsActions.archiveStarted());
  try {
    yield call(gateway.setArchived, id, archived);
    yield put(
      administratorsActions.archiveSucceeded({
        id,
        archivedAt: archived ? new Date().toISOString() : null,
      }),
    );
  } catch (e) {
    yield put(administratorsActions.archiveFailed(toMessage(e, ARCHIVE_ERROR)));
  }
}

/** WORKER: delete a row for good, dropping it from the list on success. */
function* deleteWorker(action: ReturnType<typeof deleteRequested>) {
  const { id } = action.payload;
  const gateway: AdministratorsGateway = yield getContext('administratorsGateway');
  yield put(administratorsActions.deleteStarted());
  try {
    yield call(gateway.deleteAdministrator, id);
    yield put(administratorsActions.deleteSucceeded(id));
  } catch (e) {
    yield put(administratorsActions.deleteFailed(toMessage(e, DELETE_ERROR)));
  }
}

/**
 * WORKER: count what a deletion would take. A failure is swallowed on purpose:
 * the figures only sharpen the dialog, which still says what goes without them.
 */
function* deletionImpactWorker(action: ReturnType<typeof deletionImpactRequested>) {
  const gateway: AdministratorsGateway = yield getContext('administratorsGateway');
  yield put(administratorsActions.deletionImpactStarted());
  try {
    const impact = (yield call(gateway.fetchDeletionImpact, action.payload.id)) as UserDeletionImpact;
    yield put(administratorsActions.deletionImpactLoaded(impact));
  } catch {
    // Left null: the dialog falls back to its unnumbered wording.
  }
}

/** WATCHERS. */
export function* administratorsSaga() {
  yield all([
    takeLatest(administratorsRequested.type, fetchWorker),
    takeLatest(archiveToggleRequested.type, archiveWorker),
    takeLatest(deleteRequested.type, deleteWorker),
    takeLatest(deletionImpactRequested.type, deletionImpactWorker),
  ]);
}
